// Home — lean mobile-first command center.
// Greeting · Archetype strip · 5 quick actions · slim recent carousel ·
// optional "More for you" disclosure (Alerts, Memory, Founder progress).
// Total visual density reduced ≥60% vs the prior 7-widget dashboard.
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { AppShell } from '../components/AppNav';
import { BetaWelcomeBanner } from '../components/BetaWelcomeBanner';
import { HandleClaimBanner } from '../components/HandleClaimBanner';
import { formatArchetypeTitle, formatArchetypeSubtitle } from '../lib/formatArchetype';
import { useFeatureFlag } from '../hooks/useFeatureFlag';
import { useExperienceMode } from '../hooks/useExperienceMode';

const API = window.__SG_API_BASE__;

const Kicker = ({ children, className = '' }) => (
  <p className={`font-sans text-[10px] uppercase tracking-[0.32em] text-[#C7A96B] ${className}`}>{children}</p>
);

// =========================================================================
// Building blocks
// =========================================================================
const QuickAction = ({ label, sub, path, testid, accent }) => {
  const navigate = useNavigate();
  return (
    <button
      onClick={() => navigate(path)}
      data-testid={testid}
      className={`bg-white border ${accent ? 'border-[#C7A96B]/50' : 'border-[#E5E4E2]'} rounded-2xl p-5 text-left hover:border-[#0D0D0D] hover:shadow-[0_4px_24px_-12px_rgba(0,0,0,0.15)] transition-all duration-200 min-h-[100px] flex flex-col justify-between`}
    >
      <p className="font-serif text-base text-[#0D0D0D] leading-tight">{label}</p>
      <div className="flex items-end justify-between gap-2 mt-2">
        <p className="font-sans text-[11px] text-[#8E8A84] leading-snug line-clamp-2">{sub}</p>
        <span className="font-sans text-[10px] uppercase tracking-[0.22em] text-[#C7A96B] flex-shrink-0">→</span>
      </div>
    </button>
  );
};

const ArchetypeStrip = ({ profile }) => {
  const navigate = useNavigate();
  const title = formatArchetypeTitle(
    profile?.archetype_display_name ||
    profile?.archetype_name ||
    profile?.archetype_key ||
    profile?.archetype ||
    null
  );
  const subtitle = formatArchetypeSubtitle(
    profile?.archetype_key || profile?.archetype_name || profile?.archetype || null
  );
  const colors = (profile?.best_colors || []).slice(0, 3);
  return (
    <div
      onClick={() => navigate('/profile')}
      role="button" tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate('/profile'); } }}
      data-testid="home-archetype-strip"
      className="cursor-pointer bg-white border border-[#E5E4E2] rounded-3xl p-5 sm:p-6 hover:border-[#0D0D0D] transition-colors group"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <Kicker className="mb-2">Your Style Genome</Kicker>
          <p
            className="font-serif text-[#0D0D0D] leading-[1.05] tracking-tight text-2xl sm:text-3xl md:text-4xl"
            data-testid="home-archetype-name"
          >
            {title}
          </p>
          <p
            className="font-sans text-[13px] sm:text-sm text-[#5C5852] mt-2 leading-relaxed"
            data-testid="home-archetype-subtitle"
          >
            {subtitle}
          </p>
          {colors.length > 0 && (
            <div className="flex flex-wrap gap-x-3 gap-y-1 mt-3" data-testid="home-archetype-colors">
              {colors.map((c, i) => (
                <span key={i} className="font-sans text-[10px] uppercase tracking-[0.2em] text-[#8E8A84]">
                  {c}
                </span>
              ))}
            </div>
          )}
        </div>
        <span
          className="font-sans text-[10px] uppercase tracking-[0.22em] text-[#C7A96B] flex-shrink-0 mt-1 group-hover:text-[#0D0D0D] transition-colors"
          aria-hidden="true"
        >
          Refine →
        </span>
      </div>
    </div>
  );
};

// Inline search bar — only visible when the ui_vision_v4_search flag is on.
// Tapping it focuses-and-navigates to /search with the typed query.
const HomeSearchBar = () => {
  const navigate = useNavigate();
  const [text, setText] = useState('');
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        const q = text.trim();
        navigate(q ? `/search?q=${encodeURIComponent(q)}` : '/search');
      }}
      data-testid="home-search-bar"
      className="bg-white border border-[#E5E4E2] rounded-2xl px-4 py-2.5 flex items-center gap-3 hover:border-[#0D0D0D] transition-colors"
    >
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#8E8A84"
        strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" />
      </svg>
      <input
        type="text"
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Search outfits, closet items, products, stylists, brands, boutiques, creators…"
        data-testid="home-search-input"
        aria-label="Search outfits, closet items, products, stylists, brands, boutiques, creators"
        className="flex-1 bg-transparent outline-none font-sans text-base placeholder:text-[#8E8A84] text-[#0D0D0D] py-1"
        style={{ fontSize: '16px' }}
      />
      <button type="submit" data-testid="home-search-submit"
        className="font-sans text-[10px] uppercase tracking-[0.22em] text-[#C7A96B] hover:text-[#0D0D0D]">
        Search →
      </button>
    </form>
  );
};

const RecentLooksCarousel = ({ outfits }) => {
  const navigate = useNavigate();
  if (!outfits || outfits.length === 0) return null;
  return (
    <div data-testid="home-recent-carousel">
      <div className="flex items-baseline justify-between mb-2">
        <Kicker>Recent looks</Kicker>
        <button onClick={() => navigate('/outfit-builder')} className="font-sans text-[10px] uppercase tracking-[0.22em] text-[#8E8A84] hover:text-[#0D0D0D]">
          Builder →
        </button>
      </div>
      <div className="flex gap-3 overflow-x-auto pb-1 no-scrollbar">
        {outfits.map(o => (
          <div key={o.id || o.outfit_id} data-testid={`home-recent-${o.id || o.outfit_id}`}
            onClick={() => navigate('/outfit-builder')}
            role="button" tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate('/outfit-builder'); } }}
            className="flex-shrink-0 basis-[60%] sm:basis-[40%] md:basis-[30%] max-w-[14rem] bg-white rounded-2xl p-3 border border-[#E5E4E2] hover:border-[#C7A96B]/50 transition-colors cursor-pointer">
            <p className="font-serif text-sm leading-snug line-clamp-2 text-[#0D0D0D]">
              {o.name || 'Saved outfit'}
            </p>
            <p className="font-sans text-[10px] uppercase tracking-[0.18em] text-[#8E8A84] mt-1.5">
              {(o.occasion || 'general').replace(/_/g, ' ')}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};

const MoreForYou = ({ closetSummary, closetGapCount, memory, founder }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const hasContent =
    (closetSummary?.needs_review || 0) > 0 ||
    (closetSummary?.similarities || 0) > 0 ||
    closetGapCount > 0 ||
    (memory?.insights || []).length > 0 ||
    !!founder;
  if (!hasContent) return null;
  return (
    <div data-testid="home-more-for-you">
      <button
        onClick={() => setOpen(o => !o)}
        data-testid="home-more-toggle"
        aria-expanded={open}
        className="w-full text-left flex items-center justify-between py-2 px-1 border-b border-[#E5E4E2]"
      >
        <Kicker>More for you</Kicker>
        <span className="font-sans text-[10px] uppercase tracking-[0.22em] text-[#C7A96B]">
          {open ? '— Hide' : '+ Show'}
        </span>
      </button>

      {open && (
        <div className="mt-3 space-y-3" data-testid="home-more-content">
          {/* Alerts */}
          {((closetSummary?.needs_review || 0) > 0 || (closetSummary?.similarities || 0) > 0 || closetGapCount > 0) && (
            <div className="bg-white border border-[#E5E4E2] rounded-2xl p-4" data-testid="home-alerts">
              <div className="space-y-2.5">
                {(closetSummary?.needs_review || 0) > 0 && (
                  <button onClick={() => navigate('/closet?filter=needs_review')} data-testid="home-alert-needs-review"
                    className="w-full flex items-center justify-between gap-3 hover:text-[#C7A96B]">
                    <span className="font-sans text-sm text-[#0D0D0D]">{closetSummary.needs_review} items need review</span>
                    <span className="text-[#C7A96B] text-xs">→</span>
                  </button>
                )}
                {(closetSummary?.similarities || 0) > 0 && (
                  <button onClick={() => navigate('/closet?open=similarities')} data-testid="home-alert-duplicates"
                    className="w-full flex items-center justify-between gap-3 hover:text-[#C7A96B]">
                    <span className="font-sans text-sm text-[#0D0D0D]">{closetSummary.similarities} possible duplicates</span>
                    <span className="text-[#C7A96B] text-xs">→</span>
                  </button>
                )}
                {closetGapCount > 0 && (
                  <button onClick={() => navigate('/profile#gaps')} data-testid="home-alert-gaps"
                    className="w-full flex items-center justify-between gap-3 hover:text-[#C7A96B]">
                    <span className="font-sans text-sm text-[#0D0D0D]">{closetGapCount} closet gaps</span>
                    <span className="text-[#C7A96B] text-xs">→</span>
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Memory insights */}
          {(memory?.insights || []).length > 0 && (
            <div onClick={() => navigate('/memory')} data-testid="home-memory"
              role="button" tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate('/memory'); } }}
              className="cursor-pointer bg-white border border-[#E5E4E2] rounded-2xl p-4 hover:border-[#0D0D0D]">
              <Kicker className="mb-2">Memory</Kicker>
              <ul className="space-y-1.5">
                {memory.insights.slice(0, 2).map((line, i) => (
                  <li key={i} className="font-sans text-[12px] text-[#0D0D0D] leading-snug">
                    · {line}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Founder progress (admin/founder only) */}
          {founder && (
            <div onClick={() => navigate('/profile')} data-testid="home-founder-circle"
              role="button" tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate('/profile'); } }}
              className="cursor-pointer bg-white border border-[#C7A96B]/40 rounded-2xl p-4">
              <div className="flex items-baseline justify-between mb-1">
                <Kicker>Founder</Kicker>
                <span className="font-sans text-[10px] uppercase tracking-[0.22em] text-[#C7A96B]" data-testid="founder-badge">
                  {founder.badge}
                </span>
              </div>
              <p className="font-serif text-base text-[#0D0D0D]" data-testid="founder-headline">
                {founder.completed_items}/{founder.total_items} steps complete
              </p>
              <div className="w-full h-1 bg-[#E5E4E2] rounded-full overflow-hidden mt-2" data-testid="founder-progress-bar">
                <div className="h-full bg-[#C7A96B]" style={{ width: `${founder.progress_pct}%` }} />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Style direction — Keep / Restyle / Invest guidance from the genome
// closet-action layer, with a gentle confidence indicator.
const StyleDirectionCard = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/api/genome/closet-actions`, { withCredentials: true })
      .then(r => { if (!cancelled) setData(r.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);
  if (!data) return null;
  const groups = [
    { key: 'keep', title: 'Keep', items: data.keep },
    { key: 'restyle', title: 'Restyle', items: data.restyle },
    { key: 'invest', title: 'Invest', items: data.invest },
  ].filter(g => (g.items || []).length > 0);
  if (groups.length === 0) return null;
  const conf = data.confidence || {};
  return (
    <div className="bg-white border border-[#E5E4E2] rounded-3xl p-5 sm:p-6" data-testid="home-style-direction">
      <div className="flex items-baseline justify-between mb-1.5">
        <Kicker>Style direction</Kicker>
        <span className="font-sans text-[10px] uppercase tracking-[0.22em] text-[#8E8A84]" data-testid="genome-confidence">
          Genome · {conf.level}
        </span>
      </div>
      <div className="w-full h-1 bg-[#E5E4E2] rounded-full overflow-hidden mb-4" aria-hidden="true">
        <div className="h-full bg-[#C7A96B]" style={{ width: `${conf.pct || 0}%` }} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {groups.map(g => (
          <div key={g.key} data-testid={`direction-${g.key}`}>
            <p className="font-serif text-base text-[#0D0D0D] mb-1.5">{g.title}</p>
            <ul className="space-y-1.5">
              {g.items.slice(0, 3).map((it, i) => (
                <li key={i} className="font-sans text-[12px] text-[#5C5852] leading-snug">
                  <span className="text-[#0D0D0D]">{it.name}</span> — {it.reason}
                  {g.key === 'invest' && (
                    <button
                      onClick={() => navigate(`/stylist?q=${encodeURIComponent(`I have a closet gap: ${it.name}. Help me style around it using pieces I already own first, then suggest generic (non-branded) categories worth investing in.`)}`)}
                      data-testid={`invest-gap-ask-${i}`}
                      className="block font-sans text-[10px] uppercase tracking-[0.18em] text-[#C7A96B] hover:text-[#0D0D0D] mt-1 min-h-[32px]">
                      Ask about this gap →
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
};

// =========================================================================
// Home
// =========================================================================
// Mode-Based UX Phase 1 — invite members who haven't picked an experience
// mode yet. Dismisses for the session; permanent once a mode is chosen.
const ModeSetupBanner = () => {
  const navigate = useNavigate();
  const modeFlag = useFeatureFlag('mode_based_experience_v1');
  const { ready, prefs } = useExperienceMode();
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem('sg_mode_banner_dismissed') === '1');
  if (dismissed || !modeFlag.on || !ready || prefs?.configured) return null;
  return (
    <div className="bg-[#0D0D0D] text-white rounded-3xl p-5 flex items-center justify-between gap-4" data-testid="home-mode-setup-banner">
      <div>
        <p className="font-serif text-lg">Make StyleGenome yours</p>
        <p className="font-sans text-xs text-white/70 mt-0.5">Pick your experience mode — inspiration, shopping or professional.</p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button onClick={() => navigate('/onboarding/mode')} data-testid="home-mode-setup-cta"
          className="bg-[#C7A96B] text-[#0D0D0D] px-4 py-2.5 rounded-full font-sans text-[10px] uppercase tracking-[0.16em] min-h-[40px]">
          Set up
        </button>
        <button onClick={() => { sessionStorage.setItem('sg_mode_banner_dismissed', '1'); setDismissed(true); }}
          aria-label="Dismiss" data-testid="home-mode-setup-dismiss"
          className="text-white/60 hover:text-white px-2 min-h-[40px]">✕</button>
      </div>
    </div>
  );
};

export const Home = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [profile, setProfile] = useState(null);
  const [memory, setMemory] = useState({ insights: [] });
  const [closetSummary, setClosetSummary] = useState({ total: 0, needs_review: 0, similarities: 0 });
  const [recentOutfits, setRecentOutfits] = useState([]);
  const [founder, setFounder] = useState(null);
  const [profileReady, setProfileReady] = useState(false);

  // Server-issued role only — never an email string (v4.3.1 P0 parity with backend).
  const isFounder = user?.role === 'founder';
  const isAdmin = user?.role === 'admin' || isFounder || user?.is_admin === true;

  useEffect(() => {
    let cancelled = false;
    const setP = (v) => { if (!cancelled) setProfile(v); };
    const setR = () => { if (!cancelled) setProfileReady(true); };
    axios.get(`${API}/api/profile/style`, { withCredentials: true })
      .then(r => setP(r.data))
      .catch(e => { if (e.response?.status === 404) navigate('/quiz'); })
      .finally(setR);

    axios.get(`${API}/api/feedback/style-memory`, { withCredentials: true })
      .then(r => { if (!cancelled) setMemory({ insights: (r.data?.insights || []).slice(0, 2) }); })
      .catch(() => {});

    axios.get(`${API}/api/closet/items`, { withCredentials: true })
      .then(r => {
        if (cancelled) return;
        const items = r.data?.items || [];
        const needs = items.filter(i => i.review_status === 'needs_review').length;
        setClosetSummary(s => ({ ...s, total: items.length, needs_review: needs }));
      }).catch(() => {});

    axios.get(`${API}/api/closet/similarities?status=pending`, { withCredentials: true })
      .then(r => { if (!cancelled) setClosetSummary(s => ({ ...s, similarities: r.data?.count || 0 })); })
      .catch(() => {});

    axios.get(`${API}/api/outfits`, { withCredentials: true })
      .then(r => { if (!cancelled) setRecentOutfits((r.data?.outfits || []).slice(0, 6)); })
      .catch(() => {});

    return () => { cancelled = true; };
  }, [navigate]);

  // Founder progress — show only for admin/founder per directive.
  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    axios.get(`${API}/api/founder-circle/me`, { withCredentials: true })
      .then(r => { if (!cancelled) setFounder(r.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [isAdmin]);

  const closetGapCount = (profile?.closet_gaps || []).length;
  const searchFlag = useFeatureFlag('ui_vision_v4_search');
  const shopFlag = useFeatureFlag('shop_v1');

  // Quick actions — Shop card only shows when shop_v1 is on for this user.
  const quickActions = useMemo(() => {
    const base = [
      { key: 'stylist',  label: 'Ask your Stylist',      sub: 'Adapt a look or get a recommendation', path: '/stylist',         accent: true },
      { key: 'outfit',   label: 'Build today\u2019s outfit', sub: 'Pick the occasion, we assemble the look', path: '/outfit-builder' },
      { key: 'closet',   label: 'Open your Closet',      sub: closetSummary.total ? `${closetSummary.total} pieces saved` : 'Add your first pieces', path: '/closet' },
      { key: 'social',   label: 'Explore the Style Network', sub: 'Influencers, stylists, style enthusiasts', path: '/social' },
    ];
    if (shopFlag.on) {
      base.push({ key: 'shop', label: 'Shop curated picks', sub: 'Curated product inspiration from reviewed sources', path: '/shop' });
    }
    return base;
  }, [closetSummary.total, shopFlag.on]);

  if (!profileReady) {
    return (
      <AppShell>
        <div className="min-h-screen flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-[#0D0D0D] border-t-transparent rounded-full animate-spin" />
        </div>
      </AppShell>
    );
  }

  const firstName = user?.name?.split(' ')[0] || 'there';

  return (
    <AppShell>
      <div className="bg-[#F7F5F1] min-h-dvh w-full max-w-full overflow-x-hidden">
        <BetaWelcomeBanner />
        <HandleClaimBanner />

        {/* Greeting */}
        <header className="page-pad page-top pb-1 max-w-3xl mx-auto" data-testid="home-header">
          <Kicker className="mb-2">Welcome back</Kicker>
          <h1 className="font-serif text-3xl md:text-5xl tracking-tight leading-tight" data-testid="home-greeting">
            Hello, {firstName}.
          </h1>
          <p className="font-sans text-sm text-[#8E8A84] mt-1.5">
            Your styling command center.
          </p>
        </header>

        <div className="page-pad pb-bottomnav max-w-3xl mx-auto mt-6 space-y-5" data-testid="home-page">

          <ModeSetupBanner />

          {/* Inline search bar (gated by ui_vision_v4_search) */}
          {searchFlag.on && <HomeSearchBar />}

          {/* Archetype strip — always visible; falls back to a graceful
              "Your Style Genome is forming" copy when profile is missing. */}
          <ArchetypeStrip profile={profile} />

          {/* Keep / Restyle / Invest guidance */}
          <StyleDirectionCard />

          {/* 5 quick actions */}
          <div className="grid-auto-cards" data-testid="home-quick-actions">
            {quickActions.map(a => (
              <QuickAction key={a.key}
                label={a.label} sub={a.sub} path={a.path} accent={a.accent}
                testid={`home-quick-${a.key}`} />
            ))}
          </div>

          {/* Slim recent looks carousel */}
          <RecentLooksCarousel outfits={recentOutfits} />

          {/* Optional "More for you" — collapsible, hidden by default */}
          <MoreForYou
            closetSummary={closetSummary}
            closetGapCount={closetGapCount}
            memory={memory}
            founder={isAdmin ? founder : null}
          />
        </div>
      </div>
    </AppShell>
  );
};

export default Home;
